import { useCallback, useEffect, useState } from "react";
import { message } from "antd";
import { t } from "i18next";
import { isNotNil, useSerializeCallback } from "@kalodata/tools";
import type { UploadUserVideoResponse, UserAssetRecord } from "@apis/http/api";
import { ClipModal } from "@components/ClipModal/ClipModal";
import { ClipUpload, type ClipUploadFile } from "@components/ClipUpload/ClipUpload";
import { PostToTikTokModal } from "@layouts/GlobalVideoDetailModal/PostToTikTokModal/PostToTikTokModal";
import { useTikTokAccounts } from "@pages/me/hooks/useTikTokAccounts";
import { TestId } from "@shared/testIds";
import { Trans } from "react-i18next";
import {
  POST_VIDEO_MODAL_WIDTH,
  UPLOAD_CARD_HEIGHT,
  UPLOAD_CARD_WIDTH,
  type PostVideoLibrarySubTab,
  type PostVideoSourceTab,
} from "./constants";
import { PostVideoLibraryPanel } from "./PostVideoLibraryPanel";
import { PostVideoModalFooter } from "./PostVideoModalFooter";
import { PostVideoModalHeader } from "./PostVideoModalHeader";
import { PostVideoSourceTabs } from "./PostVideoSourceTabs";
import { resolvePostVideoAssetId } from "./resolvePostVideoAssetId";
import { usePostVideoUpload } from "./usePostVideoUpload";

export interface PostVideoModalProps {
  open: boolean;
  onClose: () => void;
  onPostToTikTokClose?: () => void;
}

const resolveUploadedVideoAssetId = (file?: ClipUploadFile): number | null => {
  const response = file?.response as UploadUserVideoResponse | undefined;
  const assetId = response?.assetId;
  return typeof assetId === "number" && Number.isFinite(assetId) && assetId > 0 ? assetId : null;
};

export const PostVideoModal = ({ open, onClose, onPostToTikTokClose }: PostVideoModalProps) => {
  const [sourceTab, setSourceTab] = useState<PostVideoSourceTab>("videoFile");
  const [librarySubTab, setLibrarySubTab] = useState<PostVideoLibrarySubTab>("generated");
  const [uploadedVideoFiles, setUploadedVideoFiles] = useState<ClipUploadFile[]>([]);
  const [selectedLibraryItem, setSelectedLibraryItem] = useState<UserAssetRecord | undefined>();
  const [postToTikTokOpen, setPostToTikTokOpen] = useState(false);
  const [postToTikTokAssetId, setPostToTikTokAssetId] = useState<number | null>(null);
  const getTikTokAccounts = useTikTokAccounts();
  const { beforeUpload, uploadFile, accept } = usePostVideoUpload();

  useEffect(() => {
    if (!open) {
      setSourceTab("videoFile");
      setLibrarySubTab("generated");
      setUploadedVideoFiles([]);
      setSelectedLibraryItem(undefined);
    }
  }, [open]);

  const handlePostToTikTokClose = () => {
    setPostToTikTokOpen(false);
    setPostToTikTokAssetId(null);
    onPostToTikTokClose?.();
  };

  const openPostToTikTok = useSerializeCallback(async (assetId: number | null) => {
    if (!assetId) {
      message.error(t("Invalid asset"));
      return;
    }
    await getTikTokAccounts();
    onClose();
    setPostToTikTokAssetId(assetId);
    setPostToTikTokOpen(true);
  });

  const handleUploadedVideoFilesChange = useCallback(
    (files: ClipUploadFile[]) => {
      setUploadedVideoFiles(files);
      const assetId = resolveUploadedVideoAssetId(files[0]);
      if (assetId) {
        void openPostToTikTok(assetId);
      }
    },
    [openPostToTikTok],
  );

  const handleLibrarySubmit = useSerializeCallback(async () => {
    const assetId = selectedLibraryItem ? resolvePostVideoAssetId(selectedLibraryItem) : null;
    await openPostToTikTok(assetId);
  });

  return (
    <>
      <ClipModal
        open={open}
        onCancel={onClose}
        title={null}
        closable={false}
        mask={{ closable: false }}
        width={POST_VIDEO_MODAL_WIDTH}
        centered
        destroyOnHidden
        data-testid={TestId.PostVideoModal}
        classNames={{
          container: "p-0! flex flex-col overflow-hidden rounded-[12px]!",
          header: "hidden!",
          body: "flex min-h-0 flex-1 flex-col p-0!",
        }}
      >
        <PostVideoModalHeader onClose={onClose} />
        <PostVideoSourceTabs value={sourceTab} onChange={setSourceTab} />

        {sourceTab === "videoFile" ? (
          <>
            <div className="bg-kds-bg-subtle px-[16px] py-[12px]">
              <p className="m-0 text-[14px] leading-[1.4] text-kds-fg-soft">
                <Trans>Supports MP4, MOV (Max 100MB)</Trans>
              </p>
            </div>
            <div className="flex justify-center py-[40px] pb-[48px]">
              <ClipUpload
                files={uploadedVideoFiles}
                onFilesChange={handleUploadedVideoFilesChange}
                maxCount={1}
                accept={accept}
                beforeUpload={beforeUpload}
                uploadFile={uploadFile}
                cardWidth={UPLOAD_CARD_WIDTH}
                cardHeight={UPLOAD_CARD_HEIGHT}
                disabled={openPostToTikTok.isPending()}
              />
            </div>
          </>
        ) : (
          <>
            <PostVideoLibraryPanel
              enabled={open && sourceTab === "library"}
              subTab={librarySubTab}
              onSubTabChange={setLibrarySubTab}
              selectedId={selectedLibraryItem?.id}
              onSelect={setSelectedLibraryItem}
            />
            <PostVideoModalFooter
              submitDisabled={!selectedLibraryItem || handleLibrarySubmit.isPending()}
              onCancel={onClose}
              onSubmit={handleLibrarySubmit}
            />
          </>
        )}
      </ClipModal>

      {isNotNil(postToTikTokAssetId) && (
        <PostToTikTokModal open={postToTikTokOpen} assetId={postToTikTokAssetId} onClose={handlePostToTikTokClose} />
      )}
    </>
  );
};
